import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type BriefingSection =
  | 'header' | 'kill-sheet'
  | 'gas-infrastructure' | 'permitting' | 'physical-site'
  | 'land-ownership' | 'infrastructure' | 'power-infrastructure'
  | 'water-sourcing' | 'fiber-connectivity'
  | 'mine-hazards' | 'oil-gas-wells'
  | 'risk-register' | 'data-confidence';

export type BriefingMode = 'brief' | 'checklist';

export type HoverFeature =
  | { type: 'pipeline'; name: string; operator?: string; segmentKeys?: string[] }
  | { type: 'substation' }
  | { type: 'highway' }
  | { type: 'gas-plant' }
  | { type: 'compressor' }
  | { type: 'railroad' }
  | { type: 'fiber' }
  | { type: 'aquifer' }
  | { type: 'gas-supply' }
  | { type: 'airport' }
  | { type: 'oil-gas-wells' }
  | { type: 'flood' }
  | { type: 'wetlands' }
  | { type: 'parcel' }
  | null;

export type FootprintScreen = 500 | 1000 | null;

interface ScrollSyncContextType {
  footprintScreen: FootprintScreen;
  setFootprintScreen: (value: FootprintScreen) => void;
  activeSection: BriefingSection;
  hoverFeature: HoverFeature;
  setHoverFeature: (f: HoverFeature) => void;
  isFullView: boolean;
  setFullView: (v: boolean) => void;
  briefingMode: BriefingMode;
  setBriefingMode: (m: BriefingMode) => void;
  navigatedFromChecklist: boolean;
  scrollToSection: (section: BriefingSection, categoryName?: string) => void;
  handleScroll: (container: HTMLElement) => void;
  registerSection: (id: BriefingSection, el: HTMLElement) => void;
  unregisterSection: (id: BriefingSection) => void;
}

const ScrollSyncContext = createContext<ScrollSyncContextType>({
  footprintScreen: null,
  setFootprintScreen: () => {},
  activeSection: 'header',
  hoverFeature: null,
  setHoverFeature: () => {},
  isFullView: false,
  setFullView: () => {},
  briefingMode: 'brief',
  setBriefingMode: () => {},
  navigatedFromChecklist: false,
  scrollToSection: () => {},
  handleScroll: () => {},
  registerSection: () => {},
  unregisterSection: () => {},
});

export const useScrollSync = () => useContext(ScrollSyncContext);

export const ScrollSyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [footprintScreen, setFootprintScreen] = useState<FootprintScreen>(null);
  const [activeSection, setActiveSection] = useState<BriefingSection>('header');
  const [hoverFeature, setHoverFeature] = useState<HoverFeature>(null);
  const [isFullView, setFullViewState] = useState(() => {
    try { const v = sessionStorage.getItem('briefing-full-view'); return v === null ? true : v === '1'; } catch { return true; }
  });
  const setFullView = useCallback((v: boolean) => {
    setFullViewState(v);
    try { sessionStorage.setItem('briefing-full-view', v ? '1' : '0'); } catch {}
  }, []);
  const [briefingMode, setBriefingModeRaw] = useState<BriefingMode>('brief');
  const [navigatedFromChecklist, setNavigatedFromChecklist] = useState(false);
  const lastChecklistCategoryRef = useRef<string | null>(null);
  // Poll for an element to appear, then scroll to it. Max 20 frames (~330ms at 60fps).
  const scrollWhenReady = useCallback((finder: () => Element | null | undefined) => {
    let attempts = 0;
    const poll = () => {
      const el = finder();
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts++ < 20) {
        requestAnimationFrame(poll);
      }
    };
    requestAnimationFrame(poll);
  }, []);

  const setBriefingMode = useCallback((m: BriefingMode) => {
    setBriefingModeRaw(m);
    setNavigatedFromChecklist(false);
    // Scroll to top on mode switch
    const container = scrollContainerRef.current || document.querySelector('.briefing-content-panel');
    if (container) container.scrollTop = 0;
    // When returning to checklist, scroll to the category that was last clicked
    if (m === 'checklist' && lastChecklistCategoryRef.current) {
      const catName = lastChecklistCategoryRef.current;
      scrollWhenReady(() => document.querySelector(`[data-dd-category="${catName}"]`));
    }
  }, [scrollWhenReady]);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const sectionsRef = useRef<Map<BriefingSection, HTMLElement>>(new Map());
  const activeSectionRef = useRef<BriefingSection>('header');
  const rafRef = useRef<number>(0);

  const scrollToSection = useCallback((section: BriefingSection, categoryName?: string) => {
    // Uses setBriefingModeRaw (not setBriefingMode) to avoid scroll-to-top — we want to scroll to a specific section
    setBriefingModeRaw('brief');
    setNavigatedFromChecklist(true);
    lastChecklistCategoryRef.current = categoryName || null;
    // Poll until the section element is registered and rendered
    scrollWhenReady(() => sectionsRef.current.get(section));
  }, [scrollWhenReady]);

  const handleScroll = useCallback((container: HTMLElement) => {
    scrollContainerRef.current = container;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const rootRect = container.getBoundingClientRect();
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 20;

      if (atBottom) {
        let lastSection: BriefingSection | null = null;
        let lastTop = -Infinity;
        for (const [id, el] of sectionsRef.current) {
          const rect = el.getBoundingClientRect();
          if (rect.top > lastTop) { lastTop = rect.top; lastSection = id; }
        }
        if (lastSection && lastSection !== activeSectionRef.current) {
          activeSectionRef.current = lastSection;
          setActiveSection(lastSection);
        }
        return;
      }

      const triggerY = rootRect.top + rootRect.height * 0.35;
      let best: BriefingSection | null = null;
      let bestDist = Infinity;

      for (const [id, el] of sectionsRef.current) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= triggerY && rect.bottom > rootRect.top) {
          const dist = triggerY - rect.top;
          if (dist < bestDist) {
            bestDist = dist;
            best = id;
          }
        }
      }

      if (best && best !== activeSectionRef.current) {
        activeSectionRef.current = best;
        setActiveSection(best);
      }
    });
  }, []);

  const registerSection = useCallback((id: BriefingSection, el: HTMLElement) => {
    sectionsRef.current.set(id, el);
  }, []);

  const unregisterSection = useCallback((id: BriefingSection) => {
    sectionsRef.current.delete(id);
  }, []);

  return (
    <ScrollSyncContext.Provider value={{
        footprintScreen, setFootprintScreen, activeSection, hoverFeature, setHoverFeature, isFullView, setFullView, briefingMode, setBriefingMode, navigatedFromChecklist, scrollToSection, handleScroll, registerSection, unregisterSection }}>
      {children}
    </ScrollSyncContext.Provider>
  );
};

export const useSectionRef = (id: BriefingSection) => {
  const { registerSection, unregisterSection } = useScrollSync();
  const ref = useCallback((el: HTMLElement | null) => {
    if (el) registerSection(id, el);
    else unregisterSection(id);
  }, [id, registerSection, unregisterSection]);
  return ref;
};
